import type { LicenceId } from '@ps/platform-message-framework/common/LicenceId';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import type { Account } from '@ps/web-core-store';
import { format, parseISO } from 'date-fns';

import { composeValidatorsWithThreshold } from '../composeValidatorsWithThreshold/composeValidatorsWithThreshold';
import { mustContainLowerCaseLetter } from '../mustContainLowerCaseLetter/mustContainLowerCaseLetter';
import { mustContainOnlyEnglishCharacters } from '../mustContainOnlyEnglishCharacters/mustContainOnlyEnglishCharacters';
import { mustContainOnlyLatinCharacters } from '../mustContainOnlyLatinCharacters/mustContainOnlyLatinCharacters';
import { mustContainSymbol } from '../mustContainSymbol/mustContainSymbol';
import { mustContainUpperCaseLetter } from '../mustContainUpperCaseLetter/mustContainUpperCaseLetter';
import { mustNotContain } from '../mustNotContain/mustNotContain';
import { mustNotContainName } from '../mustNotContainName/mustNotContainName';
import { mustNotContainSymbols } from '../mustNotContainSymbols/mustNotContainSymbols';
import { mustStartWithLetter } from '../mustStartWithLetter/mustStartWithLetter';
import { poBoxAddressNotAllowed } from '../poBoxAddressNotAllowed/poBoxAddressNotAllowed';
import {
  TXTCLI_Incorrect_Information_Entered,
  TXTCLI_Your_name_and_date_of_birth_is_ELL_1,
  TXTWCL_At_least_one_capital_letter_XA_ELL,
  TXTWCL_At_least_one_symbol_XeXgX_XX,
  TXTWCL_Must_contain_at_least_3_of_the_ELL,
  TXTWCL_The_address_you_have_entered_i_ELL_1
} from '../translations';
import {
  ADDRESS_NOT_ALLOWED_SYMBOLS,
  PORTUGAL_STREET_ADDRESS_ALLOWED_CHARACTERS,
  PORTUGAL_STREET_ADDRESS_VALID_CHARACTERS
} from '../utils/regex';
import { validateAllowedCharacters } from '../validateAllowedCharacters/validateAllowedCharacters';

export interface ValidatorMapPayload {
  account: Partial<Account>;
  isRealMoneyOK: boolean;
}

const validatorMap: {
  [field in 'password' | 'userId' | 'address']: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [license in Sites | LicenceId | 'default']?: any;
  };
} = {
  password: {
    [Sites.SPA]: (payload?: ValidatorMapPayload) => {
      const validations = [
        mustContainSymbol(TXTWCL_At_least_one_symbol_XeXgX_XX),
        mustContainUpperCaseLetter(TXTWCL_At_least_one_capital_letter_XA_ELL)
      ];

      if (payload?.isRealMoneyOK) {
        const { dob } = payload.account;
        validations.push(
          composeValidatorsWithThreshold(
            [
              mustNotContain(dob && format(parseISO(dob), 'ddLLyy')),
              mustNotContainName(payload.account)
            ],
            TXTCLI_Your_name_and_date_of_birth_is_ELL_1
          )
        );
      }

      return validations;
    },
    [Sites.DE]: (payload?: ValidatorMapPayload) => {
      const validations = [
        composeValidatorsWithThreshold(
          [
            mustContainLowerCaseLetter(),
            mustContainUpperCaseLetter(),
            mustStartWithLetter(),
            mustContainSymbol()
          ],
          TXTWCL_Must_contain_at_least_3_of_the_ELL,
          3
        ),
        mustContainOnlyEnglishCharacters()
      ];

      if (payload?.isRealMoneyOK) {
        const { dob } = payload.account;
        validations.push(
          composeValidatorsWithThreshold(
            [
              mustNotContain(dob && format(parseISO(dob), 'ddLLyy')),
              mustNotContainName(payload.account)
            ],
            TXTCLI_Your_name_and_date_of_birth_is_ELL_1
          )
        );
      }

      return validations;
    },
    default: []
  },
  userId: {
    [Sites.SPA]: () => [mustContainOnlyEnglishCharacters()],
    default: []
  },
  address: {
    [Sites.US_NJ]: () => [poBoxAddressNotAllowed],
    [Sites.CA_ON]: () => [poBoxAddressNotAllowed],
    [Sites.CZE]: () => [
      mustNotContainSymbols(ADDRESS_NOT_ALLOWED_SYMBOLS, TXTCLI_Incorrect_Information_Entered)
    ],
    [Sites.POR]: () => [
      validateAllowedCharacters(
        PORTUGAL_STREET_ADDRESS_ALLOWED_CHARACTERS,
        PORTUGAL_STREET_ADDRESS_VALID_CHARACTERS
      )
    ],
    [Sites.GR]: () => [
      mustContainOnlyLatinCharacters(),
      mustNotContainSymbols(
        ADDRESS_NOT_ALLOWED_SYMBOLS,
        TXTWCL_The_address_you_have_entered_i_ELL_1
      )
    ],
    default: [
      mustNotContainSymbols(
        ADDRESS_NOT_ALLOWED_SYMBOLS,
        TXTWCL_The_address_you_have_entered_i_ELL_1
      )
    ]
  }
};

export const getValidatorsForLicense = (
  license: Sites | LicenceId,
  field: keyof typeof validatorMap,
  payload?: ValidatorMapPayload
) =>
  validatorMap[field][license]
    ? validatorMap[field][license](payload)
    : validatorMap[field].default;
